"use client";

import React, { useCallback, useState } from "react";
import { AlertTriangle, Mic, MicOff, Send, X } from "lucide-react";

import type useGeminiWs from "@/hooks/use-gemini-ws";
import type { Conversation } from "@/hooks/use-webrtc";
import ChatMessages from "../voice-agent/ChatMessages";
import { imageStyle } from "@/lib/embed-image";

export type GeminiWs = ReturnType<typeof useGeminiWs>;

/**
 * 会話がまだ無いときに出す操作の案内。
 *
 * 挨拶と違ってボットの発言ではないので、吹き出しにはせず控えめに置く。
 * 公開ページと埋め込みで同じ文面にするため、ここに1つだけ持つ。
 */
const OPERATION_HINT =
  "質問を入力して送信してください。\nマイクのボタンを押すと音声でも話しかけられます。";

/** 入力欄と、待機中に名前の下に出る行。どちらも埋め込み先から差し替えられる */
const INPUT_PLACEHOLDER = "メッセージを入力";
const IDLE_STATUS = "テキストで質問できます";

export type GeminiChatPanelProps = {
  gemini: GeminiWs;
  agentName: string;
  avatarUrl: string;
  /** false なら埋め込み先の --showcan-avatar を見ない。既定は見る */
  avatarOverridable?: boolean;
  /** 管理画面の挨拶メッセージ。回答と同じ吹き出しで最初に出す */
  greeting?: string;
  /** 入力欄のプレースホルダ */
  inputPlaceholder?: string;
  /**
   * 待機中に名前の下へ出す行。
   * 接続中・読み上げ中・マイクがオンの3つは状態の知らせなので差し替えない
   */
  idleStatus?: string;
  /** 埋め込みウィジェットでは閉じるボタンを出す。公開ページでは出さない */
  onClose?: () => void;
  /** 読み上げの聞き直し。埋め込みでは訪問者向けの導線を増やさないため出さない */
  showReplay?: boolean;
  /** レイテンシとトークン数の実測値。訪問者に見せるものではないので既定はオフ */
  showMetrics?: boolean;
};

/**
 * Gemini音声チャットの画面。公開ページと埋め込みウィジェットで共有する。
 *
 * 音声とテキストをモードで分けず、1本の入力バーに並べている。
 * 話しかけたい人はマイク、打ちたい人は入力欄と、切り替え操作を挟まずに使えるため。
 */
export default function GeminiChatPanel({
  gemini,
  agentName,
  avatarUrl,
  avatarOverridable = true,
  greeting,
  inputPlaceholder = INPUT_PLACEHOLDER,
  idleStatus = IDLE_STATUS,
  onClose,
  showReplay = false,
  showMetrics = false,
}: GeminiChatPanelProps) {
  const [input, setInput] = useState("");

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    gemini.sendTextMessage(text);
    setInput("");
  }, [input, gemini.sendTextMessage]);

  // 入力を始めたらマイクを止める。つけたままだと打鍵音を発話として拾う
  const handleInputChange = useCallback(
    (value: string) => {
      setInput(value);
      if (value && gemini.isRecording) {
        gemini.stopRecording();
      }
    },
    [gemini.isRecording, gemini.stopRecording],
  );

  const handleToggleRecording = useCallback(() => {
    if (gemini.isRecording) {
      gemini.stopRecording();
      return;
    }
    // マイクをオンにするときは入力途中のテキストを残さない
    setInput("");
    void gemini.startRecording();
  }, [gemini.isRecording, gemini.startRecording, gemini.stopRecording]);

  // 確定済みの会話に、進行中の文字起こし・回答を仮の行として足して表示する
  const conversation: Conversation[] = (() => {
    const messages: Conversation[] = gemini.conversation.map((item) => ({
      role: item.role,
      text: item.text,
      timestamp: item.timestamp,
      isFinal: true,
      audioUrl: item.audioUrl,
      products: item.products,
    }));

    if (gemini.currentTranscript) {
      messages.push({
        role: "user",
        text: gemini.currentTranscript,
        timestamp: new Date().toISOString(),
        isFinal: false,
      });
    } else if (gemini.isSpeechPending) {
      messages.push({ role: "user", text: "", timestamp: new Date().toISOString(), isFinal: false });
    }

    if (gemini.currentAnswer) {
      messages.push({
        role: "assistant",
        text: gemini.currentAnswer,
        timestamp: new Date().toISOString(),
        isFinal: false,
      });
    }

    return messages;
  })();

  const statusText =
    gemini.status === "connecting"
      ? "接続中..."
      : gemini.isSpeaking
        ? "読み上げ中です"
        : gemini.isRecording
          ? "マイクがオンです。話しかけてください"
          : idleStatus;

  return (
    <div className="showcan-chat flex min-h-0 flex-1 flex-col bg-white">
      <header className="showcan-header flex items-center gap-3 border-b border-slate-200 px-4 py-3">
        {/* ランチャーと同じく、埋め込み先が --showcan-avatar で差し替えられるよう背景で置く */}
        <div
          role="img"
          aria-label={agentName}
          className="showcan-avatar h-9 w-9 shrink-0 rounded-full bg-[var(--showcan-ink-20)] bg-cover bg-center"
          style={imageStyle("--showcan-avatar", avatarUrl, avatarOverridable)}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">{agentName}</p>
          <p className="text-[11px] text-slate-400">{statusText}</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="showcan-close flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </header>

      {gemini.warning && (
        <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{gemini.warning}</span>
        </div>
      )}

      <ChatMessages
        conversation={conversation}
        agentName={agentName}
        avatarUrl={avatarUrl}
        showReplay={showReplay}
        greeting={greeting}
        emptyMessage={OPERATION_HINT}
      />

      <div className="showcan-composer flex items-center gap-2 border-t border-slate-200 px-3 py-3">
        <button
          type="button"
          onClick={handleToggleRecording}
          disabled={gemini.status !== "ready"}
          data-recording={gemini.isRecording ? "true" : "false"}
          className={`showcan-mic flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
            gemini.isRecording ? "bg-[var(--showcan-ink)] text-white" : "bg-slate-100 text-slate-500"
          }`}
          aria-label={gemini.isRecording ? "マイクをオフにする" : "マイクをオンにする"}
        >
          {gemini.isRecording ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </button>

        <input
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={inputPlaceholder}
          disabled={gemini.status !== "ready"}
          className="showcan-input min-w-0 flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700 outline-none focus:border-[var(--showcan-ink)] disabled:bg-slate-50"
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={gemini.status !== "ready" || !input.trim()}
          className="showcan-send flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--showcan-ink)] text-white transition-colors disabled:opacity-40"
          aria-label="送信"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      {showMetrics && gemini.metrics && (
        <div className="showcan-metrics border-t border-slate-200 bg-slate-50 px-4 py-2 font-mono text-[10px] leading-relaxed text-slate-500">
          <div>
            応答開始 {gemini.metrics.responseStartMs}ms / TTFT {gemini.metrics.ttftMs}ms / 合計{" "}
            {gemini.metrics.totalMs}ms
          </div>
          <div>
            入力 {gemini.metrics.promptTokens}tok（キャッシュ {gemini.metrics.cachedTokens}tok） / 出力{" "}
            {gemini.metrics.outputTokens}tok
          </div>
        </div>
      )}
    </div>
  );
}
